import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  Row,
  UiBox,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {ACNE_TELECONSULTATION_KEYS, NO_OPTION, YES_OPTION} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire9Screen = () => {
  const rules = {
    medicationsTaking: () => {
      if (inputs?.selectedOption === YES_OPTION) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {
            minimum: 5,
            maximum: 300,
            tooShort: '^This field is too short (minimum is %{count} characters)',
            tooLong: '^This field is too long (maximum is %{count} characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          },
        };
      }
      return undefined;
    },
  };

  const dispatch: AppDispatch = useDispatch();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === ACNE_TELECONSULTATION_KEYS.ON_ACNE_OR_SKIN_TREATMENT);
  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[ACNE_TELECONSULTATION_KEYS.ON_ACNE_OR_SKIN_TREATMENT]?.data,
  );

  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      selectedOption: currentAnswer?.selectedOption || '',
      medicationsTaking: currentAnswer?.medicationsTaking || '',
      medicationWork: currentAnswer?.medicationWork || '',
      sideEffects: currentAnswer?.sideEffects || '',
    },
    {rules},
  );

  const onClickSubmit = () => {
    if (inputs.selectedOption === YES_OPTION) {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    dispatch(
      setQuestionnaireAnswer({
        key: ACNE_TELECONSULTATION_KEYS.ON_ACNE_OR_SKIN_TREATMENT,
        index: questionnaire?.index || '',
        data: inputs,
      }),
    );
    dispatch(setNextQuestionnaire({value: ACNE_TELECONSULTATION_KEYS.OTHER_MEDICATIONS_OR_SUPPLEMENTS}));
  };

  const onClickOption = (value: string) => {
    if (value === NO_OPTION) {
      setInputs({selectedOption: value, medicationsTaking: '', medicationWork: '', sideEffects: ''});
    }
    setInputs({selectedOption: value});
  };

  const onClickYesNoOption = (key: string, value: string) => {
    setInputs({[key]: value});
  };

  const renderOptions = () => {
    if (inputs.selectedOption === YES_OPTION) {
      const selectedOptionData = questionnaire?.options?.find(i => i.value === inputs.selectedOption);
      return (
        <UiBox w="100%" my={spacing.small}>
          {selectedOptionData?.relations?.map(i => {
            if (i?.elementType?.name === 'inputText') {
              return (
                <UiBox
                  w="100%"
                  mt={spacing.small}
                  key={i?.key || i?.elementType?.name}
                  className="animate__animated animate__fadeInLeft"
                >
                  <UiText fontSize="md" fontWeight="600" my={spacing.small}>
                    {i?.questions?.default}
                  </UiText>
                  <UiFormControl id="medicationsTaking" isRequired isInvalid={!!validation?.errors?.medicationsTaking}>
                    <UiTextarea
                      mt={spacing.small}
                      bg="white"
                      borderRadius={spacing.tiny}
                      placeholder={i?.elementType?.placeholder}
                      value={inputs.medicationsTaking}
                      onChange={({target}) => setInputs({medicationsTaking: target.value})}
                      minH="200px"
                    />
                    {validation?.errors?.medicationsTaking && (
                      <UiFormErrorMessage>{validation?.errors?.medicationsTaking[0]}</UiFormErrorMessage>
                    )}
                  </UiFormControl>
                </UiBox>
              );
            }
            if (i?.elementType?.type === 'OPTION') {
              return (
                <>
                  <UiText fontSize="md" fontWeight="bold" my={spacing.medium}>
                    {i?.questions?.default}
                  </UiText>
                  <Row>
                    {i?.options?.map(value => (
                      <ScrollToBottomButton
                        key={`${i.key}-${value.label}-${value.value}`}
                        title={value.label}
                        variant={inputs?.[i.key as keyof typeof inputs] === value.value ? 'solid' : 'toggle'}
                        onClick={() => onClickYesNoOption(i.key as string, value.value as string)}
                        height="35px"
                        w="100%"
                        mb={5}
                        mx={2}
                      />
                    ))}
                  </Row>
                </>
              );
            }
            return null;
          })}
        </UiBox>
      );
    } else {
      return (
        <UiBox mt={spacing.small} className="animate__animated animate__fadeInLeft" w="100%">
          {questionnaire?.descriptions?.default?.map(val => (
            <UiText key={val} mt={spacing.small} textColor="liteOuter">
              {val}
            </UiText>
          ))}
        </UiBox>
      );
    }
  };
  const valid =
    inputs?.selectedOption === YES_OPTION ? Object.values(inputs).every(el => !!el) : !!inputs?.selectedOption;
  return (
    <UiQuestionnaireLayout
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
      actions={questionnaire?.actions}
      disableNext={!valid}
    >
      <Col align="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.small} />
        <UiText fontSize="xl" fontWeight="bold" my={spacing.medium}>
          {questionnaire?.questions?.default}
        </UiText>
        <Row w="100%">
          {questionnaire?.options?.map(value => (
            <ScrollToBottomButton
              key={`${value.label}-${value.value}`}
              title={value.label}
              variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
              onClick={() => onClickOption(value.value as string)}
              height="35px"
              w="100%"
              mb={5}
              mx={2}
            />
          ))}
        </Row>
        {renderOptions()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire9Screen;
